import Button from '@/components/Button';
import ArgumentInputRowLabel from '@/components/arguments/ArgumentInputRowLabel';
import NetSuiteCustomArguments from '@/components/arguments/argumentCustomArguments/NetSuiteCustomArguments';
import LexicalInput from '@/components/lexicalInput/LexicalInput';
import CustomArgumentsModal from '@/components/modals/CustomArgumentsModal';
import { FiStar } from '@/components/react-icons/main';
import RowV2 from '@/components/v2/RowV2';
import useNetSuiteChangeAction from '@/hooks/useNetSuiteChangeAction.js';
import { AddIcon, DeleteIcon } from '@/lib/constants/icons';
import getLookupMethod from '@/lib/getLookupMethod';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import argumentDataSources from '@/schema/argumentDataSources';
import appActions from '@/screens/apps/redux/actions/appActions';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import { activeWorkflowStep } from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import dataTypeIcons from '@/screens/workflows/schema/dataTypeIcons';
import dataTypeTitles from '@/screens/workflows/schema/dataTypeTitles';
import { getDeep } from '@shared/utils/getDeep.js';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const isObjectWithKeys = (obj) => {
  return typeof obj === 'object' && obj !== null && Object.keys(obj).length > 0;
};

const ArgumentCustomArguments = ({ isSimpleArguments, path = 'options.customArguments', parentKey, level, definition = {}, dataSource, dataSavePath, disabled = false }) => {
  const [loadingRequired, setLoadingRequired] = useState(false);
  const options = useSelector(argumentSelectors[dataSource].activeOptions);
  const args = useSelector(argumentSelectors[dataSource].activeArguments());
  const step = dataSource === argumentDataSources.workflowStep ? useSelector(activeWorkflowStep) : {};
  const { isNetsuiteChange } = useNetSuiteChangeAction({ dataSource });
  const customArguments = useMemo(() => {
    if (path === 'arguments' && isObjectWithKeys(step)) {
      return getDeep(path, step);
    }
    if (path === 'arguments') {
      return args;
    }
    return options?.customArguments || getDeep(path, step) || {};
  }, [path, step, options, args]);
  const customArgumentsSettings = definition?.features?.customArguments;
  const isLookup = customArgumentsSettings?.type === 'lookup';
  const lookupMethod = useMemo(() => getLookupMethod(dataSource, isLookup), [dataSource, isLookup]);
  const { lookup, reload } = lookupMethod({ id: 'customArguments', lookupSettings: customArgumentsSettings });
  const formattedLookup = isLookup ? lookup?.map((n) => ({ ...n, label: n.label || n.value })) : null;
  const stepDataSources = [argumentDataSources.workflowStep, argumentDataSources.workflowVariables, `${argumentDataSources.workflowStep}-customArguments`];
  const actions = stepDataSources.includes(dataSource) ? stepActions : appActions;

  const customArgumentsArray = useMemo(() => {
    let result = !customArguments
      ? []
      : Object.keys(customArguments).map((key) => {
          const argValue = customArguments[key] || {};
          return {
            key,
            value: argValue,
            custom: argValue?.custom,
            required: argValue?.required,
            label: formattedLookup?.length ? formattedLookup.find((n) => n.value === key)?.label || key : key
          };
        });

    if (parentKey) {
      // filter only the custom arguments that are direct children of the parentKey
      const parentKeyStr = String(parentKey);
      result = result
        .filter((n) => {
          if (!n?.key) return false;
          const keyStr = String(n.key);
          return keyStr.startsWith(`${parentKeyStr}.`) && keyStr.split('.').length === parentKeyStr.split('.').length + 1 && n.custom === true;
        })
        .map((n) => ({
          ...n,
          key: String(n.key).split('.').slice(-1)[0],
          label: (n.label ? String(n.label) : n.key).split('.').slice(-1)[0]
        }));
    }
    return result;
  }, [customArguments, parentKey, formattedLookup]);

  const requiredArgs =
    isLookup && lookup?.length > 0
      ? lookup?.filter(
          (n) =>
            n.required && //
            !customArgumentsArray.find((m) => !!m.key && m.key.toLowerCase() === n.value.toLowerCase()) &&
            !definition?.arguments?.find((m) => !!m.key && m?.key?.toLowerCase() === n?.value?.toLowerCase())
        )
      : [];

  const getIcon = (arg) => {
    return !arg?.value?.dataType ? FiStar : dataTypeIcons[arg?.value?.dataType];
  };

  const getIconTitle = (arg) => {
    return !arg?.value?.dataType ? 'Text' : dataTypeTitles[arg?.value?.dataType];
  };

  const getLexicalActions = (arg) => {
    return !arg?.value?.required ? [{ icon: DeleteIcon, onClick: (e) => handleDelete(e, arg.key), className: 'inactive', title: 'Delete Argument' }] : [];
  };

  const handleAdd = () => {
    if (!lookup?.length) {
      reload?.();
    }
    setModalState('customArguments', { open: true, isLookup, lookupId: 'customArguments', dataSource, dataSavePath, parentKey, path, initialArguments: customArgumentsArray });
  };

  const handleEdit = ({ key, required }, value) => {
    const argKey = parentKey ? `${parentKey}.${key}` : key;
    const payload = { ...value, custom: true };
    if (required) {
      payload.required = true;
    }

    actions.set(dataSavePath ? `${dataSavePath}.${path}.["${argKey}"]` : `${path}.["${argKey}"]`, payload);
  };

  const handleDelete = (event, key) => {
    event?.stopPropagation();
    const argKey = parentKey ? `${parentKey}.${key}` : key;

    actions.set(dataSavePath ? `${dataSavePath}.${path}` : path, (prev) => {
      const { [argKey]: deleted, ...rest } = prev;
      return rest;
    });
  };

  // hook to add required custom fields
  useEffect(() => {
    const fetchRequiredArgs = async () => {
      if (requiredArgs.length > 0) {
        setLoadingRequired(true);

        const newRequiredCustomArgs = {};
        requiredArgs.forEach((n) => {
          newRequiredCustomArgs[n.value] = { type: 'lexical', value: '', custom: true, required: true };
        });

        await actions.set(dataSavePath ? `${dataSavePath}.${path}` : path, (prev) => ({ ...prev, ...newRequiredCustomArgs }));
        setLoadingRequired(false);
      }
    };

    fetchRequiredArgs();
  }, [requiredArgs, customArgumentsArray, definition?.arguments, path]);

  return (
    <>
      {isNetsuiteChange && <NetSuiteCustomArguments path={path} dataSource={dataSource} dataSavePath={dataSavePath} disabled={disabled} />}
      {!isNetsuiteChange && (
        <>
          {customArgumentsArray?.map((arg, i) => (
            <RowV2 key={`${arg.key}-${i}`} variant={`workflow-panel custom-arg ${isSimpleArguments ? 'simple-arguments' : ''} ${level ? `level-${level}` : ''}`}>
              <ArgumentInputRowLabel label={arg.label} dataTitle={arg.key} icon={getIcon(arg)} title={getIconTitle(arg)} isRequired={arg.value?.required} />
              <LexicalInput id={arg.key} argKey={arg.key} variation={'workflow'} value={arg?.value} placeholder={'Value'} debounce={500} onDebounce={(value) => handleEdit(arg, value)} actions={getLexicalActions(arg)} useDataTypes={true} disabled={disabled} />
            </RowV2>
          ))}
          <Button icon={AddIcon} onClick={handleAdd} className={`medium ${level ? `level-${level}` : ''}`} size={'sm'} loading={loadingRequired} disabled={disabled}>
            {loadingRequired ? 'Fetching Required Fields' : 'Add Custom Field'}
          </Button>
          <CustomArgumentsModal />
        </>
      )}
    </>
  );
};

export default ArgumentCustomArguments;
